import * as B from '@blueprintjs/core';
import classnames from 'classnames';
import * as geojson from 'geojson';
import * as I from 'immutable';
import React from 'react';

import {ApiFeature} from 'app/modules/Remote/Feature';
import {ApiFeatureCollection} from 'app/modules/Remote/FeatureCollection';
import {useUserInfo} from 'app/providers/AuthProvider';
import {FeaturesActions} from 'app/providers/FeaturesProvider';
import {NotesActions, NotesState, StateApiNote, getAlertNoteType} from 'app/stores/NotesStore';
import {USER_ROLE_REGULAR} from 'app/utils/constants';
import * as mapUtils from 'app/utils/mapUtils';
import * as noteUtils from 'app/utils/noteUtils';
import {publishPopoverMaxHeight} from 'app/utils/popoverUtils';
import {atLeastUserRole} from 'app/utils/userUtils';

import AlertCard from '../NoteCard/AlertCard';
import paneCs from '../NotesReports/styles.module.scss';
import {
  SidebarActionBar,
  SidebarActionBarHint,
  SidebarActionBarLabel,
  SidebarCardList,
  SidebarPanel,
  SidebarToolbar,
} from '../Sidebar';
import {FilterOption} from './alertFilterOptions';
import {useAlertPolicies} from './AlertPolicyProvider';
import {countEnabledLookouts} from './alertPolicyUtils';
import {AlertsPoliciesPanel} from './AlertsPoliciesPanel';
import {ALERT_TYPE_CONFIGURATIONS, findAlertPolicyForNote, getAlertTypeLabel} from './alertTypes';
import LookoutIcon from './LookoutIcon';
import cs from './styles.module.scss';
import {useAlertReviewState} from './useAlertReviewState';

export interface Props {
  feature: I.ImmutableOf<ApiFeature>;
  featureCollection: I.ImmutableOf<ApiFeatureCollection>;
  allFeatures: I.ImmutableListOf<ApiFeature>;
  notesState: NotesState;
  notesActions: NotesActions;
  onSelectImageRefs: (imageRefs: mapUtils.MapImageRefs) => void;
  zoomToGeometry: (g: geojson.GeoJSON) => unknown;
  onSelectNote: (nId: number | string) => unknown;
  updateFeature: FeaturesActions['updateFeature'];
  enrolledLayerKeys: string[];
}

const Alerts: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  feature,
  featureCollection,
  allFeatures,
  notesState,
  notesActions,
  updateFeature,
  onSelectImageRefs,
  zoomToGeometry,
  onSelectNote,
  enrolledLayerKeys,
}) => {
  const noteScrollContainer = React.useRef<HTMLDivElement>(null);
  const focusedNoteCardRef = React.useRef<HTMLDivElement>(null);
  const prevFocusedNoteIdRef = React.useRef<string | number | null>(notesState.focusedNoteId);
  const allJSFeatures = React.useMemo(() => allFeatures.toJS(), [allFeatures]);

  const [, profile] = useUserInfo();
  const canModifyAlerts = atLeastUserRole(USER_ROLE_REGULAR, profile!);

  const {
    alertPolicies,
    availableNotes,
    visibleNotes,
    targetNotes,
    selectedNotes,
    allSelected,
    isBulkTarget,
    filterOptions,
    activeFilter,
    sortDirection,
    onFocusNote,
    toggleNoteSelected,
    toggleFilter,
    toggleSortDirection,
    selectAll,
    clearTarget,
    saveTarget,
    dismissTarget,
  } = useAlertReviewState({
    feature,
    notesState,
    notesActions,
    onSelectImageRefs,
    zoomToGeometry,
    onSelectNote,
    canModifyAlerts,
  });

  const enabledLookoutCount = React.useMemo(
    () => countEnabledLookouts(feature, alertPolicies),
    [feature, alertPolicies]
  );

  // Handles alerts focused by clicking on the map
  React.useEffect(() => {
    // If a focusedNoteId is set, and is different from a previous focused note, scroll to the new note.
    if (focusedNoteCardRef && prevFocusedNoteIdRef.current !== notesState.focusedNoteId) {
      noteUtils.scrollToFocusedNote(focusedNoteCardRef, noteScrollContainer);
    }
    // Update our ref so that we can detect new values
    prevFocusedNoteIdRef.current = notesState.focusedNoteId;
  }, [notesState.focusedNoteId]);

  // Cleanup function for component unmount
  React.useEffect(
    () => () => notesActions.stopEditingPendingGeometry(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const showReviewToolbar = enabledLookoutCount > 0 || availableNotes.length > 0;

  if (notesState.loading) {
    // Render loading container.
    return (
      <div className={cs.loadingContainer}>
        <B.Spinner />
      </div>
    );
  }

  return (
    <SidebarPanel
      header={
        <AlertsPoliciesPanel
          {...{
            updateFeature,
            feature,
            featureCollection,
            allFeatures: allJSFeatures,
            enabledLookoutCount,
          }}
        />
      }
      toolbar={
        showReviewToolbar ? (
          <ReviewToolbar
            visibleNotes={visibleNotes}
            selectedNotes={selectedNotes}
            allSelected={allSelected}
            canModifyAlerts={canModifyAlerts}
            onSelectAll={selectAll}
            filterOptions={filterOptions}
            activeFilter={activeFilter}
            toggleFilter={toggleFilter}
            sortDirection={sortDirection}
            toggleSortDirection={toggleSortDirection}
          />
        ) : undefined
      }
      actionBar={
        visibleNotes.length > 0 ? (
          <ReviewActionBar
            targetNotes={targetNotes}
            isBulkTarget={isBulkTarget}
            canModifyAlerts={canModifyAlerts}
            alertPolicies={alertPolicies}
            onSave={saveTarget}
            onDismiss={dismissTarget}
            onClear={clearTarget}
          />
        ) : undefined
      }
      bodyBackground="offWhite"
      bodyClassName={cs.feed}
      bodyRef={noteScrollContainer}
    >
      <>
        {visibleNotes.length === 0 &&
          (!showReviewToolbar ? (
            <B.NonIdealState
              className={paneCs.bodyEmpty}
              iconMuted={false}
              icon={<LookoutIcon size={60} />}
            >
              <p>
                Automatically look out for changes on your property. We’ll notify you as soon as
                anything happens so you can rest easy.
              </p>
              <p>
                <strong>Enroll in a preset policy or create your own!</strong>
              </p>
            </B.NonIdealState>
          ) : (
            <div className={cs.emptyState}>No new changes detected on this property.</div>
          ))}

        <SidebarCardList>
          {visibleNotes.map((note) => (
            <AlertCard
              key={note.id}
              note={note}
              cardRef={note.id === notesState.focusedNoteId ? focusedNoteCardRef : undefined}
              toggleSelected={toggleNoteSelected}
              canModifyAlerts={canModifyAlerts}
              onSelectNote={onFocusNote}
              isFocused={note.id === notesState.focusedNoteId}
              onHoverNote={() => notesActions.hoverNote(note.id)}
              onUnhoverNote={() => notesActions.hoverNote(null)}
              isSelected={selectedNotes.has(note.id)}
              enrolledLayerKeys={enrolledLayerKeys}
              onSelectImageRefs={onSelectImageRefs}
              featureGeometry={feature.get('geometry').toJS()}
            />
          ))}
        </SidebarCardList>
      </>
    </SidebarPanel>
  );
};

export interface ReviewToolbarProps {
  visibleNotes: StateApiNote[];
  selectedNotes: Set<number | string>;
  allSelected: boolean;
  canModifyAlerts: boolean;
  onSelectAll: () => void;
  filterOptions: FilterOption[];
  activeFilter: FilterOption;
  toggleFilter: (id: string) => void;
  sortDirection: 'asc' | 'desc';
  toggleSortDirection: () => void;
}

export const ReviewToolbar = ({
  visibleNotes,
  selectedNotes,
  allSelected,
  canModifyAlerts,
  onSelectAll,
  filterOptions,
  activeFilter,
  toggleFilter,
  sortDirection,
  toggleSortDirection,
}: ReviewToolbarProps) => {
  const filterOptionsContent = (
    <B.Menu className={cs.filterMenu}>
      {filterOptions.map((fo) => (
        <B.MenuItem
          key={fo.id}
          icon={fo.icon ? React.cloneElement(fo.icon, {color: fo.color}) : 'blank'}
          text={fo.label}
          labelElement={fo.id === activeFilter.id ? <B.Icon icon="tick" /> : undefined}
          onClick={() => toggleFilter(fo.id)}
        />
      ))}
    </B.Menu>
  );

  return (
    <SidebarToolbar
      leading={
        <B.Checkbox
          checked={allSelected}
          indeterminate={selectedNotes.size > 0 && !allSelected}
          onChange={onSelectAll}
          disabled={!canModifyAlerts}
          aria-label="Select all changes"
        />
      }
      label={
        selectedNotes.size > 0 ? (
          `${selectedNotes.size} selected`
        ) : (
          <>
            Review<span className={cs.countChip}>{visibleNotes.length}</span>
          </>
        )
      }
      actions={
        <B.ButtonGroup minimal>
          <B.Tooltip content="Sort by date">
            <B.Button
              icon={sortDirection === 'asc' ? 'sort-asc' : 'sort-desc'}
              onClick={toggleSortDirection}
            />
          </B.Tooltip>
          <B.Popover
            content={filterOptionsContent}
            minimal
            modifiersCustom={[publishPopoverMaxHeight]}
            position={B.PopoverPosition.BOTTOM_RIGHT}
            rootBoundary="viewport"
          >
            <B.Button icon="filter">
              <span className={cs.filterLabel}>{activeFilter.label}</span>
            </B.Button>
          </B.Popover>
        </B.ButtonGroup>
      }
    />
  );
};

export interface ReviewActionBarProps {
  targetNotes: StateApiNote[];
  isBulkTarget: boolean;
  canModifyAlerts: boolean;
  alertPolicies: ReturnType<typeof useAlertPolicies>['alertPolicies'];
  onSave: () => void;
  onDismiss: () => void;
  onClear: () => void;
}

export const ReviewActionBar = ({
  targetNotes,
  isBulkTarget,
  canModifyAlerts,
  alertPolicies,
  onSave,
  onDismiss,
  onClear,
}: ReviewActionBarProps) => {
  if (!targetNotes.length) {
    return (
      <SidebarActionBarHint
        shortcuts={
          canModifyAlerts ? 'J next · K previous · S save · D dismiss · X select' : undefined
        }
      >
        {canModifyAlerts
          ? 'Click a card to review it on the map, then save or dismiss'
          : 'Open a change to view it on the map'}
      </SidebarActionBarHint>
    );
  }

  const count = targetNotes.length;
  const singleNote = count === 1 ? targetNotes[0] : undefined;
  const singleConfig = singleNote ? ALERT_TYPE_CONFIGURATIONS[getAlertNoteType(singleNote)] : null;
  const singlePolicy = singleNote ? findAlertPolicyForNote(singleNote, alertPolicies) : undefined;

  return (
    <SidebarActionBar
      context={
        <>
          {singleConfig && <B.Icon icon={singleConfig.icon} size={14} color={singleConfig.color} />}
          <SidebarActionBarLabel>
            {singleNote && singleConfig
              ? getAlertTypeLabel(getAlertNoteType(singleNote), singlePolicy)
              : `${count} selected`}
          </SidebarActionBarLabel>
        </>
      }
      contextAction={
        <B.Button minimal small text={isBulkTarget ? 'Clear' : 'Close'} onClick={onClear} />
      }
    >
      <B.Button intent="primary" icon="annotation" onClick={onSave} disabled={!canModifyAlerts}>
        {count > 1 ? `Save ${count} as notes` : 'Save as note'}
        {canModifyAlerts && <span className={classnames(cs.buttonKey, cs.buttonKeyOnDark)}>S</span>}
      </B.Button>
      <B.Button icon="trash" onClick={onDismiss} disabled={!canModifyAlerts}>
        {count > 1 ? `Dismiss ${count}` : 'Dismiss'}
        {canModifyAlerts && (
          <span className={classnames(cs.buttonKey, cs.buttonKeyOnLight)}>D</span>
        )}
      </B.Button>
    </SidebarActionBar>
  );
};

export default Alerts;
